import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  AfterViewInit,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { CopilotFacadeService } from '../../services/copilot-facade.service';
import {
  ChatSuggestionsService,
  type ChatSuggestionItem,
} from '../../services/chat-suggestions.service';
import { Message } from '../../models/copilot.models';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, switchMap, of, map, skip, tap, distinctUntilChanged, startWith, catchError } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { Splitter } from 'primeng/splitter';
import { Button } from 'primeng/button';
import { MarkdownModule, MarkdownService } from 'ngx-markdown';
import {
  AuthService,
  OktaUserCacheService,
  OktaUserInfo,
} from '@brightside/core/auth';
import { ClipboardService } from '@brighthub/utils';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { ChatSuggestionsBarComponent } from '../chat-suggestions-bar/chat-suggestions-bar.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { CopilotActions } from '../../store/copilot.actions';
import {
  selectChatSearchState,
  selectInputTextForConversation,
} from '../../store/copilot.selectors';
import { resolveChatSuggestionAgentId } from '../../config/chat-suggestion-agents.config';
import {
  computeChatSearchMatches,
  injectMarkdownSearchMarks,
  normalizeActiveMatchIndex,
} from '../../utils/chat-search.utils';
import { User } from '@sentry/types';

@Component({
  selector: 'lib-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Splitter,
    MarkdownModule,
    Button,
    ChatSuggestionsBarComponent,
    ChatMessageComponent,
    ScrollRegionComponent,
  ],
  providers: [Store],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements AfterViewInit {

  /** When true, hides the chat input panel (e.g. conversation/employee layouts). */
  readonly disableChat = input<boolean>(false);

  private _clipboardService = inject(ClipboardService);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);
  private readonly _host = inject(ElementRef<HTMLElement>);

  readonly chatSearchInput =
    viewChild<ElementRef<HTMLInputElement>>('chatSearchInput');

  readonly messageInput =
    viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');

  readonly facade = inject(CopilotFacadeService);
  private readonly _chatSuggestions = inject(ChatSuggestionsService);
  readonly auth = inject(AuthService);
  readonly oktaUserCache = inject(OktaUserCacheService);
  readonly messages$ = this.facade.messagesForSelectedConversation$;
  messages: Signal<Message[]> = toSignal(this.messages$, { initialValue: [] });

  messagesWithDisplay = computed(() =>
    this.messages().map(msg => {
      const user = this.oktaUserCache.getUser(msg.owner_id ?? '');
      return {
        ...msg,
        displayName: user?.firstName ?? '',
        initials: this._getInitials(user),
      };
    })
  );

  /** Bumps when auth profile is ready so suggestion fetch can retry after token hydration. */
  private readonly _suggestionsAuthReady$ = new Subject<void>();

  readonly selectedClientId$ = this.facade.selectedClientId$;
  readonly selectedConversationId$ = this.facade.selectedConversation$;
  readonly selectedClient$ = this.facade.selectedClient$;

  readonly selectedClientId = toSignal(this.selectedClientId$);
  readonly selectedConversationId = toSignal(this.selectedConversationId$);

  readonly inputText = signal('');

  /** Suggestion chips from GET /v1/prototype/chat/suggestions; empty hides the row. */
  readonly chatSuggestions = signal<ChatSuggestionItem[]>([]);

  /** Same streaming/disabled rules as the Send button (chips must not fire while BrightNav is thinking). */
  readonly suggestionChipsDisabled = computed(
    () => this.disableInput() || this.forceDisableInput()
  );

  /**
   * Display name for the assistant (chat role label, avatar alt, "is thinking…" text).
   */
  readonly copilotName = signal('Navi');
  readonly userDisplayName = signal('User');
  readonly userFullName = signal('Brightside User');
  readonly loggedInUserId = signal('');
  readonly disableInput = signal(false);

  readonly userInitials = computed(() => {
    const names = this.userFullName().trim().split(' ');
    return names
      .slice(0, 2)
      .map((n) => n.charAt(0).toUpperCase())
      .join('');
  });

  readonly isStreaming$: Observable<boolean> =
    this.selectedConversationId$.pipe(
      switchMap((conversationId) =>
        conversationId ? this.facade.isStreaming$(conversationId) : of(false)
      ),
      tap((streaming) => {
        this.disableInput.set(streaming);
      })
    );

  readonly showThinking$ = combineLatest([
    this.isStreaming$,
    this.messages$,
  ]).pipe(
    map(([isStreaming, messages]) => {
      if (!isStreaming) return false;
      const lastAssistant = [...messages]
        .reverse()
        .find((m) => m.role === 'assistant' && m.metadata?.finalized === false);
      return !lastAssistant || !lastAssistant.content?.trim();
    })
  );

  forceDisableInput = signal(false);
  forceHideInput = signal(false);

  /** Tracks last [conversationId, messageCount] to detect initial load only. */
  private _scrollState: { conversationId: string | null; count: number } = {
    conversationId: null,
    count: 0,
  };

  /** True after we've scrolled to bottom on initial load; keeps messages hidden until then to avoid jump. */
  readonly messagesScrollReady = signal(false);

  /** True when messages are present but we haven't scrolled to bottom yet (hide to avoid jump on first paint). */
  readonly hideMessagesUntilScrolled = computed(
    () => this.messages().length > 0 && !this.messagesScrollReady()
  );

  /** Set true briefly when stream ends so the last assistant avatar can wiggle. */
  readonly streamJustEnded = signal(false);

  /** Id of the last assistant message in the list (for wiggle target when stream ends). */
  readonly lastAssistantMessageId = computed(() => {
    const list = this.messages();
    const last = [...list].reverse().find((m) => m.role === 'assistant');
    return last?.id ?? null;
  });

  private _wasStreaming = false;

  /** True when the user has scrolled up so we show the "scroll to bottom" floating button. */
  readonly showScrollToBottomButton = signal(false);

  private static readonly SCROLL_BOTTOM_THRESHOLD = 80;

  readonly chatSearchFromStore = toSignal(
    this.store.select(selectChatSearchState),
    { initialValue: { query: '', activeMatchIndex: 0 } }
  );

  readonly searchExpanded = signal(false);
  readonly chatSearchDraft = signal('');

  /**
   * True when the user last clicked inside `.chat-messages-scroll` for this chat.
   * Enables Cmd/Ctrl+F to open in-chat search instead of the browser find bar.
   */
  readonly messagesPanelKeyboardScopeActive = signal(false);

  readonly committedChatSearchMatches = computed(() =>
    computeChatSearchMatches(
      this.messages(),
      this.chatSearchFromStore().query.trim()
    )
  );

  readonly searchMatchSummary = computed(() => {
    const matches = this.committedChatSearchMatches();
    const idx = normalizeActiveMatchIndex(
      this.chatSearchFromStore().activeMatchIndex,
      matches.length
    );
    return {
      total: matches.length,
      current: matches.length ? idx + 1 : 0,
    };
  });

  readonly viewMessages$ = combineLatest([
    this.messages$,
    this.store.select(selectChatSearchState),
  ]).pipe(
    map(([messages, search]) => {
      const query = search.query.trim();
      const allMatches = computeChatSearchMatches(messages, query);
      const norm = normalizeActiveMatchIndex(
        search.activeMatchIndex,
        allMatches.length
      );
      const activeHit = allMatches[norm];
      const activeGlobal = activeHit ? activeHit.globalIndex : -1;

      return messages.map((message) => {
        const user = this.oktaUserCache.getUser(message.owner_id ?? '');
        const ranges = allMatches
          .filter((m) => m.messageId === message.id)
          .map((m) => ({
            globalIndex: m.globalIndex,
            start: m.start,
            end: m.end,
          }));

        const markdownBody =
          query && ranges.length > 0
            ? injectMarkdownSearchMarks(
                message.content,
                ranges,
                activeGlobal
              )
            : undefined;

        return {
          ...message,
          userDisplayName: user?.firstName ?? '',
          userInitials: this._getInitials(user),
          markdownBody,
        };
      });
    })
  );

  constructor() {
    combineLatest([
      this.messages$,
      this.selectedConversationId$,
      this.isStreaming$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([messages, conversationId, isStreaming]) => {
        const wasStreaming = this._wasStreaming;
        this._wasStreaming = isStreaming;
        if (wasStreaming && !isStreaming) {
          this.streamJustEnded.set(true);
          setTimeout(() => this.streamJustEnded.set(false), 1600);
        }

        const count = messages.length;
        const prev = this._scrollState;
        const isNewConversation = conversationId !== prev.conversationId;
        const hasNewMessage = count > prev.count;
        const isInitialLoad =
          isNewConversation || (prev.count === 0 && count > 0);

        this._scrollState = { conversationId: conversationId ?? null, count };

        if (isNewConversation) {
          this.messagesScrollReady.set(false);
        }

        if (isInitialLoad && count > 0) {
          this.messagesScrollReady.set(false);
          queueMicrotask(() =>
            this.scrollToBottom('auto', () =>
              this.messagesScrollReady.set(true)
            )
          );
        } else if (isInitialLoad) {
          this.messagesScrollReady.set(true);
          queueMicrotask(() => this.scrollToBottom());
        } else if (hasNewMessage && !isStreaming) {
          queueMicrotask(() => this.scrollToBottom());
        }
      });

    this.auth.getUser().then((user) => {
      if (user) {
        this.userDisplayName.set(user.given_name || 'User');
        this.userFullName.set(`${user.given_name} ${user.family_name}`);
        this.loggedInUserId.set(user.sub);
        this._suggestionsAuthReady$.next();
      }
    });

    // Sync inputText from store when client/conversation changes (single subscription, cleaned up on destroy)
    combineLatest([this.selectedClientId$, this.selectedConversationId$])
      .pipe(
        switchMap(([clientId, conversationId]) =>
          clientId && conversationId
            ? this.store.select(selectInputTextForConversation(clientId, conversationId))
            : of('')
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((text) => this.inputText.set(text));

    // Close in-chat search UI when switching client or conversation (store is per-conversation; panel/draft are local).
    combineLatest([this.selectedClientId$, this.selectedConversationId$])
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchExpanded.set(false);
        this.chatSearchDraft.set('');
        this.messagesPanelKeyboardScopeActive.set(false);
      });

    effect(() => {
      this.forceDisableInput.set(this.facade.disableInput());
    });

    effect(() => {
      this.forceHideInput.set(this.disableChat() || this.facade.hideInput());
    });

    // Keep `disableInput` in sync for Send + suggestion chips even if template async pipes change.
    this.isStreaming$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    combineLatest([
      this.selectedConversationId$,
      this.facade.selectedConversationRow$,
      this._suggestionsAuthReady$.pipe(startWith(undefined)),
    ])
      .pipe(
        map(([conversationId, conversation]) => {
          if (!conversationId) {
            return null;
          }
          return resolveChatSuggestionAgentId(conversation);
        }),
        distinctUntilChanged(),
        switchMap((agentId) =>
          agentId
            ? this._chatSuggestions.getSuggestions(agentId).pipe(catchError(() => of([])))
            : of([] as ChatSuggestionItem[])
        ),
        catchError(() => of([] as ChatSuggestionItem[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => this.chatSuggestions.set(items));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this._attachScrollListener(), 100);
  }

  /** Native viewport owned by the shared BDS scroll region. */
  private _getMessagesScrollEl(): HTMLElement | null {
    const el =
      this._host.nativeElement.querySelector('.chat-messages-scroll');
    return el instanceof HTMLElement ? el : null;
  }

  private _attachScrollListener(): void {
    const container = this._getMessagesScrollEl();
    if (!container) return;

    const update = () => this._updateScrollToBottomVisible(container);
    container.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);

    this.destroyRef.onDestroy(() => {
      container.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    });

    update();
  }

  private _updateScrollToBottomVisible(container: HTMLElement): void {
    const { scrollTop, clientHeight, scrollHeight } = container;
    const atBottom =
      scrollTop + clientHeight >=
      scrollHeight - ChatComponent.SCROLL_BOTTOM_THRESHOLD;
    this.showScrollToBottomButton.set(!atBottom);
  }

  send(): void {
    const content = this.inputText().trim();
    if (!content) return;

    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();

    if (clientId && conversationId) {
      this.facade.sendMessage(clientId, conversationId, content, undefined, this.loggedInUserId());

      // Clear state and local signal
      this.inputText.set('');
      this.store.dispatch(CopilotActions.clearInputText({ clientId, conversationId }));
      this.scrollToBottom();
    }
  }

  /**
   * Prefills the chat textarea with a suggestion macro so the user can review or edit
   * before sending via {@link send}. Replaces any existing draft for the conversation.
   *
   * @param macro Full prompt text from the chip; internal newlines are preserved.
   */
  applySuggestionMacro(macro: string): void {
    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();
    if (
      !clientId ||
      !conversationId ||
      this.suggestionChipsDisabled()
    ) {
      return;
    }
    this.inputText.set(macro);
    this.store.dispatch(
      CopilotActions.updateInputText({ clientId, conversationId, text: macro })
    );
    this._focusMessageInput();
  }

  /** Focuses the message textarea and moves the caret to the end after a chip prefills it. */
  private _focusMessageInput(): void {
    afterNextRender(
      () => {
        const el = this.messageInput()?.nativeElement;
        if (!el) {
          return;
        }
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      },
      { injector: this._injector }
    );
  }

  handleEnter(event: Event, selectedConversationId: string): void {
    const keyBoardEvent = event as KeyboardEvent;
    if (!keyBoardEvent.shiftKey) keyBoardEvent.preventDefault();

    if (
      this.inputText().trim() &&
      selectedConversationId &&
      !this.disableInput()
    ) {
      this.send();
    }
  }

  onInputChange(event: Event): void {
    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();
    const text = (event.target as HTMLTextAreaElement).value;

    this.inputText.set(text);

    if (clientId && conversationId) {
      this.store.dispatch(CopilotActions.updateInputText({ clientId, conversationId, text }));
    }
  }

  toggleSearchExpanded(): void {
    const next = !this.searchExpanded();
    this.searchExpanded.set(next);
    if (next) {
      this.chatSearchDraft.set(this.chatSearchFromStore().query);
      this._focusSearchInputAfterOpen();
    }
  }

  private _focusSearchInputAfterOpen(): void {
    afterNextRender(
      () => {
        const input = this.chatSearchInput()?.nativeElement;
        input?.focus();
        input?.select();
      },
      { injector: this._injector }
    );
  }

  /** Open in-chat search or focus its field (used by Cmd/Ctrl+F). */
  private _openOrFocusInChatSearch(): void {
    if (!this.searchExpanded()) {
      this.searchExpanded.set(true);
      this.chatSearchDraft.set(this.chatSearchFromStore().query);
      this._focusSearchInputAfterOpen();
    } else {
      const input = this.chatSearchInput()?.nativeElement;
      input?.focus();
      input?.select();
    }
  }

  closeSearchPanel(): void {
    this.searchExpanded.set(false);
    this.chatSearchDraft.set('');
    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();
    if (clientId && conversationId) {
      this.store.dispatch(
        CopilotActions.clearChatSearch({ clientId, conversationId })
      );
    }
  }

  onChatSearchDraftInput(value: string): void {
    this.chatSearchDraft.set(value);
  }

  runChatSearch(): void {
    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();
    const draft = this.chatSearchDraft().trim();
    if (!clientId || !conversationId) return;

    this.store.dispatch(
      CopilotActions.updateChatSearchQuery({
        clientId,
        conversationId,
        query: draft,
      })
    );
    const matches = computeChatSearchMatches(this.messages(), draft);
    const expectedGlobal =
      matches.length > 0 ? matches[0].globalIndex : undefined;
    this._scheduleScrollActiveSearchHitIntoView(expectedGlobal);
  }

  navigateChatSearchMatch(delta: number): void {
    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();
    if (!clientId || !conversationId) return;

    const query = this.chatSearchFromStore().query.trim();
    const matches = computeChatSearchMatches(this.messages(), query);
    if (!matches.length) return;

    const len = matches.length;
    const state = this.chatSearchFromStore();
    const next =
      (((state.activeMatchIndex + delta) % len) + len) % len;

    const expectedGlobal = matches[next].globalIndex;

    this.store.dispatch(
      CopilotActions.setChatSearchActiveMatchIndex({
        clientId,
        conversationId,
        activeMatchIndex: next,
      })
    );
    this._scheduleScrollActiveSearchHitIntoView(expectedGlobal);
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerdownForMessagesScope(ev: PointerEvent): void {
    const host = this._host.nativeElement;
    const t = ev.target;
    if (!(t instanceof Node)) return;
    if (!host.contains(t)) {
      this.messagesPanelKeyboardScopeActive.set(false);
      return;
    }
    if (t instanceof HTMLElement) {
      const inMessages = t.closest('.chat-messages-scroll');
      this.messagesPanelKeyboardScopeActive.set(!!inMessages);
    }
  }

  /** Cmd/Ctrl+F opens in-chat search when the message list is “active”; arrow keys step matches. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydownForSearch(ev: KeyboardEvent): void {
    if (
      (ev.ctrlKey || ev.metaKey) &&
      !ev.shiftKey &&
      !ev.altKey &&
      (ev.key === 'f' || ev.key === 'F')
    ) {
      if (this.messagesPanelKeyboardScopeActive()) {
        ev.preventDefault();
        this._openOrFocusInChatSearch();
      }
      return;
    }

    if (this.searchExpanded() && ev.key === 'Escape') {
      ev.preventDefault();
      this.closeSearchPanel();
      return;
    }

    if (!this.searchExpanded()) return;
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;

    const query = this.chatSearchFromStore().query.trim();
    if (!query || this.committedChatSearchMatches().length === 0) return;

    if (ev.target instanceof HTMLElement) {
      if (ev.target.closest('form.chat-input')) return;
    }

    let delta = 0;
    switch (ev.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        delta = 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        delta = -1;
        break;
      default:
        return;
    }

    const onSearchField =
      ev.target instanceof HTMLElement &&
      ev.target.classList.contains('chat-search-input');
    if (onSearchField && (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight')) {
      return;
    }

    ev.preventDefault();
    this.navigateChatSearchMatch(delta);
  }

  /**
   * Scrolls `.chat-messages-scroll` to the active `<mark>`.
   *
   * When `expectedGlobalIndex` is set, we wait until `chat-search-hit-{n}` on the
   * active mark matches (so we don’t scroll to the *previous* hit while markdown
   * catches up). If those classes are missing (sanitizer), we only verify on
   * retries and finally **force** scroll on the last attempt.
   *
   * We do **not** skip when the hit looks “fully visible”: that check used the
   * *old* active mark when hit-classes were stripped, so we never scrolled after
   * the DOM updated.
   */
  private _scheduleScrollActiveSearchHitIntoView(
    expectedGlobalIndex?: number
  ): void {
    afterNextRender(
      () => this._runScrollToActiveSearchHit(expectedGlobalIndex),
      { injector: this._injector }
    );
  }

  private _runScrollToActiveSearchHit(expectedGlobalIndex?: number): void {
    const wantClass =
      expectedGlobalIndex !== undefined
        ? `chat-search-hit-${expectedGlobalIndex}`
        : undefined;

    let scrolled = false;
    const delaysMs = [0, 16, 50, 100, 200, 350, 500, 700];

    const scrollNow = (force: boolean): void => {
      if (scrolled) return;

      const container = this._getMessagesScrollEl();
      if (!container) return;

      const target = container.querySelector('.chat-search-mark-active');
      if (!(target instanceof HTMLElement)) return;

      if (!force && wantClass) {
        const hasHit = Array.from(target.classList).some((c) =>
          c.startsWith('chat-search-hit-')
        );
        if (hasHit && !target.classList.contains(wantClass)) {
          return;
        }
        if (!hasHit) {
          return;
        }
      }

      scrolled = true;

      const maxScroll = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      );
      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const topPad = 20;

      if (maxScroll <= 0) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        return;
      }

      const nextTop =
        container.scrollTop +
        (targetRect.top - containerRect.top) -
        topPad;
      const clamped = Math.max(0, Math.min(nextTop, maxScroll));

      container.scrollTo({ top: clamped, behavior: 'smooth' });
    };

    delaysMs.forEach((ms, i) => {
      const force = i === delaysMs.length - 1;
      setTimeout(() => scrollNow(force), ms);
    });
  }

  scrollToBottom(
    behavior: 'smooth' | 'auto' = 'smooth',
    onScrolled?: () => void
  ): void {
    const run = () => {
      const container = this._getMessagesScrollEl();
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior,
        });
      }
      onScrolled?.();
    };

    if (behavior === 'auto') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => run());
      });
    } else {
      setTimeout(run, 100);
    }
  }

  copyToClipboard(text: string): void {
    this._clipboardService.copy(text);
  }

  private _getInitials(user?: OktaUserInfo) {
    if (user) {
      return `${user.firstName.slice(0,1)}${user.lastName.slice(0,1)}`;
    }
    return 'BU';
  }

}
